import type {
  GetOrderResponse,
  PriceSimulationMeterSubTypeDto,
  PriceSimulationMeterTypeDto,
} from '@eneco/dc-be/client';

import { START_DATE_QUESTION_ASAP } from './constants';
import type { PriceSimulationFormSchema } from '../../price-simulation/schemas/priceSimulationFormSchema';
import { loadPriceSimulationState } from '../../price-simulation/utils/priceSimulationLocalStorage';
import { OrderManagementFormSchema, PROFESSIONAL_USER_OPTION_YES } from '../types/orderManagementSchema';
import { resolveVatStatusFromCompanyDetails } from './vatStatus';

const PROFESSIONAL_USER_OPTION_NO = 'No';

const PRICE_SIM_METER_TYPE_TO_ORDER: Record<string, PriceSimulationMeterTypeDto> = {
  digitalMeter: 'Digital',
  analogMeter: 'Analogue',
  biDirectionalMeter: 'BiDirectional',
};

const mapMeterType = (meterType?: string): PriceSimulationMeterTypeDto | undefined =>
  meterType ? PRICE_SIM_METER_TYPE_TO_ORDER[meterType] : undefined;

const mapMeterSubType = (meterSubTypeRegister?: string): PriceSimulationMeterSubTypeDto | undefined =>
  meterSubTypeRegister === 'Single' ? 'Single' : meterSubTypeRegister ? 'Multiple' : undefined;

export const BOILER_INSTALLATION_DATE_QUESTION_MAP = {
  Installed: 'Yes',
  NotInstalled: 'No',
  NotYetInstalled: 'NotYetInstalled',
} as const;

export const BOILER_INSTALLATION_DATE_QUESTION_TO_STATUS = {
  Yes: 'Installed',
  No: 'NotInstalled',
  NotYetInstalled: 'NotYetInstalled',
} as const;

export type OrderManagementDefaultValues = {
  [K in keyof OrderManagementFormSchema]?: OrderManagementFormSchema[K] extends Record<string, unknown>
    ? Partial<OrderManagementFormSchema[K]>
    : OrderManagementFormSchema[K];
};

const hasGasProductFromPriceSimulation = (formValues: Partial<PriceSimulationFormSchema> | undefined): boolean =>
  formValues?.energyType === 'Gas' || formValues?.energyType === 'Both';

export const getOrderManagementDefaultValues = () => {
  const electricityDetailsStepWithEanDefault = (
    step: Partial<NonNullable<OrderManagementDefaultValues['electricityDetailsStep']>>,
  ): OrderManagementDefaultValues['electricityDetailsStep'] => ({
    ...step,
    eanCodeOfMeter: step.eanCodeOfMeter ?? 'EAN',
  });

  const gasDetailsStepWithEanDefault = (
    step: Partial<NonNullable<OrderManagementDefaultValues['gasDetailsStep']>>,
  ): OrderManagementDefaultValues['gasDetailsStep'] => ({
    ...step,
    eanCodeOfMeter: step.eanCodeOfMeter ?? 'EAN',
  });

  const saved = loadPriceSimulationState();
  const formValues: Partial<PriceSimulationFormSchema> | undefined = saved?.formValues;
  if (!formValues) {
    return {
      hasSelectedGasProduct: false,
      electricityDetailsStep: electricityDetailsStepWithEanDefault({}),
      gasDetailsStep: {},
      selectPaymentMethod: {
        paymentMethod: 'DirectDebit',
      },
    } satisfies OrderManagementDefaultValues;
  }

  const includeGasEanDefaults = hasGasProductFromPriceSimulation(formValues);

  const deliveryAddressStep = {
    ...(formValues.postalCode != null && { postalCode: formValues.postalCode }),
    ...(formValues.city != null && { city: formValues.city }),
    ...(formValues.isApiPostalCodeCityValid != null && {
      isApiPostalCodeCityValid: formValues.isApiPostalCodeCityValid,
    }),
    correspondenceAddress: {
      isCorrespondenceAddressSameAsDeliveryAddress: true,
    },
  };

  const electricityMeterType = mapMeterType(formValues.meterType);
  const electricityDetailsStep =
    electricityMeterType != null ||
    formValues.hasExclusiveNightMeter != null ||
    formValues.meterSubTypeRegister != null ||
    formValues.installationCheck != null ||
    formValues.solarPanelInstallDate != null ||
    formValues.compensationType != null ||
    formValues.electricityConsumption != null
      ? {
          ...(electricityMeterType != null && { meterType: electricityMeterType }),
          ...(formValues.hasExclusiveNightMeter != null && {
            hasExclusiveNightMeter: formValues.hasExclusiveNightMeter,
          }),
          ...(mapMeterSubType(formValues.meterSubTypeRegister) != null && {
            meterSubType: mapMeterSubType(formValues.meterSubTypeRegister),
          }),
          ...(formValues.installationCheck != null && {
            hasSolarPanels: formValues.installationCheck.includes('SolarPanels'),
          }),
          ...(formValues.solarPanelInstallDate != null && {
            solarPanelInstallationDate: formValues.solarPanelInstallDate,
          }),
          ...(formValues.compensationType != null && { compensationType: formValues.compensationType }),
          ...(formValues.electricityConsumption != null && {
            ...(formValues.electricityConsumption.day != null && {
              dayElectricityConsumption: formValues.electricityConsumption.day,
            }),
            ...(formValues.electricityConsumption.night != null && {
              nightElectricityConsumption: formValues.electricityConsumption.night,
            }),
            ...(formValues.electricityConsumption.exclusiveNight != null && {
              excludingNightElectricityConsumption: formValues.electricityConsumption.exclusiveNight,
            }),
          }),
          ...(formValues.electricityConsumption?.day != null ||
          formValues.electricityConsumption?.night != null ||
          formValues.electricityConsumption?.exclusiveNight != null
            ? { meterReadingsKnown: true as const }
            : {}),
        }
      : undefined;

  const gasMeterType = mapMeterType(formValues.meterType);
  const gasDetailsStep =
    gasMeterType != null
      ? {
          meterType: gasMeterType,
        }
      : undefined;

  const electricityDetailsStepFromPriceSim =
    electricityDetailsStep && Object.keys(electricityDetailsStep).length > 0 ? electricityDetailsStep : undefined;
  const gasDetailsStepFromPriceSim =
    gasDetailsStep && Object.keys(gasDetailsStep).length > 0 ? gasDetailsStep : undefined;

  return {
    ...(deliveryAddressStep && Object.keys(deliveryAddressStep).length > 0 && { deliveryAddressStep }),
    hasSelectedGasProduct: includeGasEanDefaults,
    electricityDetailsStep: electricityDetailsStepWithEanDefault(electricityDetailsStepFromPriceSim ?? {}),
    gasDetailsStep: includeGasEanDefaults
      ? gasDetailsStepWithEanDefault(gasDetailsStepFromPriceSim ?? {})
      : (gasDetailsStepFromPriceSim ?? {}),
    selectPaymentMethod: {
      paymentMethod: 'DirectDebit',
    },
  } satisfies OrderManagementDefaultValues;
};

/**
 * Maps a backend order (getEnecoBeXapiSiteApiV1OrdersByOrderId) to partial form values.
 * Only steps and fields present on the order are included; null/undefined are omitted.
 */
export const getOrderManagementDefaultValuesFromOrder = (order: GetOrderResponse): OrderManagementDefaultValues => {
  const result: OrderManagementDefaultValues = {};
  const {
    personalDetails,
    companyDetails,
    deliveryAddress,
    correspondenceAddress,
    correspondenceAddressIsSame,
    electricity,
    gas,
    payment,
  } = order;

  if (personalDetails) {
    result.personalDetailsStep = {
      ...(personalDetails.firstName && { firstName: personalDetails.firstName }),
      ...(personalDetails.lastName && { lastName: personalDetails.lastName }),
      ...(personalDetails.dateOfBirth && { dateOfBirth: new Date(personalDetails.dateOfBirth) }),
      ...(personalDetails.email && { email: personalDetails.email }),
      ...(personalDetails.phoneNumber && { phoneNumber: personalDetails.phoneNumber }),
      ...(personalDetails.receiveInvoicesThroughMail != null && {
        receiveInvoicesThroughMail: personalDetails.receiveInvoicesThroughMail,
      }),
    } as OrderManagementDefaultValues['personalDetailsStep'];
  }

  if (companyDetails) {
    result.personalDetailsStep = {
      ...result.personalDetailsStep,
      professionalUser: order.customerType === 'SOHO' ? PROFESSIONAL_USER_OPTION_YES : PROFESSIONAL_USER_OPTION_NO,
      companyName: companyDetails.companyName,
      companyType: companyDetails.companyType,
      VATNumber: companyDetails.vatNumber ?? companyDetails.companyNumber?.toString() ?? null,
      vatStatus: resolveVatStatusFromCompanyDetails(companyDetails),
    } as OrderManagementDefaultValues['personalDetailsStep'];
  } else if (result.personalDetailsStep || personalDetails) {
    result.personalDetailsStep = { ...result.personalDetailsStep, professionalUser: PROFESSIONAL_USER_OPTION_NO };
  }

  if (deliveryAddress) {
    result.deliveryAddressStep = {
      street: deliveryAddress.street,
      houseNumber: parseInt(deliveryAddress.houseNumber),
      postalCode: deliveryAddress.postalCode,
      city: deliveryAddress.municipality,
      busNumber: deliveryAddress.bus ?? undefined,
      isApiPostalCodeCityValid: true,
      correspondenceAddress: {
        isCorrespondenceAddressSameAsDeliveryAddress: correspondenceAddressIsSame ?? true,
        ...(correspondenceAddress && {
          street: correspondenceAddress.street,
          houseNumber: correspondenceAddress.houseNumber ? parseInt(correspondenceAddress.houseNumber) : undefined,
          postalCode: correspondenceAddress.postalCode,
          city: correspondenceAddress.municipality,
          country: correspondenceAddress.country,
          busNumber: correspondenceAddress.bus ?? undefined,
          isApiPostalCodeCityValid: true,
        }),
      },
    };
  }

  if (electricity) {
    result.electricityDetailsStep = {
      ...result.electricityDetailsStep,
      reason: electricity.switchReason,
      startDate: electricity.deliveryStartDate,
      startDateQuestion: electricity.deliveryStartDateAsap ? START_DATE_QUESTION_ASAP : 'chooseStartDate',
      meterType: electricity.meterType,
      ...(electricity.ean
        ? { eanCodeOfMeter: 'EAN' as const, eanCode: electricity.ean }
        : electricity.meterNumber
          ? { eanCodeOfMeter: 'MeterNumber' as const, meterNumber: electricity.meterNumber }
          : { eanCodeOfMeter: 'Unknown' as const }),
      ...(electricity.exclusiveNightEan && {
        hasExclusiveNightMeter: true,
        eanCodeNightExclusiveOfMeter: 'EAN' as const,
        eanCodeNightExclusiveCode: electricity.exclusiveNightEan,
      }),
      ...(electricity.exclusiveNightMeterNumber && {
        hasExclusiveNightMeter: true,
        eanCodeNightExclusiveOfMeter: 'MeterNumber' as const,
        eanCodeNightExclusiveMeterNumber: electricity.exclusiveNightMeterNumber,
      }),
      ...(!electricity.ean && !electricity.meterNumber && { eanCodeOfMeter: 'Unknown' as const }),
      ...(electricity.solarPanels && {
        hasSolarPanels: electricity.solarPanels.hasSolarPanels,
        solarPanelInstallationDate: electricity.solarPanels.installationDate,
      }),
    } as OrderManagementDefaultValues['electricityDetailsStep'];
  }

  result.hasSelectedGasProduct = Boolean(gas);

  if (gas) {
    result.gasDetailsStep = {
      ...result.gasDetailsStep,
      startDate: gas.deliveryStartDate,
      startDateQuestion: gas.deliveryStartDateAsap ? START_DATE_QUESTION_ASAP : 'chooseStartDate',
      ...(gas.switchReason && { reason: gas.switchReason }),
      ...(gas.ean && { eanCodeOfMeter: 'EAN' as const, eanCode: gas.ean }),
      ...(gas.meterNumber && { eanCodeOfMeter: 'MeterNumber' as const, meterNumber: gas.meterNumber }),
      ...(!gas.ean && !gas.meterNumber && { eanCodeOfMeter: 'Unknown' as const }),
      ...(gas.maintenance && {
        boilerBrand: gas.maintenance.brand,
        homeOlderThanTenYears: gas.maintenance.buildingOlderThan10Years,
        boilerInstallationDateQuestion:
          BOILER_INSTALLATION_DATE_QUESTION_MAP[gas.maintenance.installationStatus || 'NotInstalled'],
        boilerServiceOption: gas.maintenance.maintenanceStartAsap ? START_DATE_QUESTION_ASAP : 'chooseStartDate',
        installationDate: gas.maintenance.installationDate,
        maintenanceDate: gas.maintenance.maintenanceStartDate,
      }),
    } as OrderManagementDefaultValues['gasDetailsStep'];
  }

  if (payment) {
    result.selectPaymentMethod = {
      paymentMethod: payment.paymentMethod,
      ...(payment.iban && { ibanNumber: payment.iban }),
    } as OrderManagementDefaultValues['selectPaymentMethod'];
  }

  return result;
};

function setNestedValues(
  setValue: (path: string, value: unknown, opts?: { shouldValidate?: boolean }) => void,
  pathPrefix: string,
  defaults: Record<string, unknown>,
): void {
  for (const [key, value] of Object.entries(defaults)) {
    const path = pathPrefix ? `${pathPrefix}.${key}` : key;
    const isLeaf =
      value === null ||
      value === undefined ||
      typeof value !== 'object' ||
      Array.isArray(value) ||
      value instanceof Date;
    if (!isLeaf) {
      setNestedValues(setValue, path, value as Record<string, unknown>);
    } else if (value !== undefined && value !== null) {
      setValue(path, value, { shouldValidate: false });
    }
  }
}

/**
 * Applies order-mapped values to the form via setValue.
 * Overwrites fields present on the order; leaves form values for absent order data unchanged.
 */
export const applyOrderDefaultsToForm = (
  order: GetOrderResponse,
  setValue: (path: string, value: unknown, opts?: { shouldValidate?: boolean }) => void,
): void => {
  const defaults = getOrderManagementDefaultValuesFromOrder(order);
  for (const [stepKey, stepValues] of Object.entries(defaults)) {
    if (stepValues && typeof stepValues === 'object') {
      setNestedValues(setValue, stepKey, stepValues as Record<string, unknown>);
    }
  }
};
